import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ReportUserButton } from "@/components/ReportUserDialog";
import { BlockUserButton } from "@/components/BlockUserButton";
import SocialLinksEditor, { SocialLinksList } from "@/components/SocialLinksEditor";
import ShareButton from "@/components/ShareButton";
import { Trophy, ShieldCheck } from "lucide-react";

export default function Profile() {
  const { id } = useParams();
  const { t } = useI18n();
  const { user: authUser } = useAuth();
  const [user, setUser] = useState(null);

  useEffect(() => {
    api.get(`/users/${id}`).then((r) => setUser(r.data)).catch(() => {});
  }, [id]);

  if (!user) return <div className="min-h-screen bg-[#F7F5F0]"><AppHeader /><div className="p-10 text-slate-500">{t("loading")}</div></div>;
  const isSelf = authUser && authUser.user_id === user.user_id;

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="profile-page">
      <AppHeader />
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <BackButton className="mb-4" testid="profile-back-btn" />
        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <div className="flex items-start gap-5 flex-wrap">
            <Avatar className="w-20 h-20">
              <AvatarImage src={user.picture} />
              <AvatarFallback className="bg-emerald-100 text-emerald-800 text-2xl font-semibold">{user.name?.[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-semibold" data-testid="profile-role">{t(user.role) || user.role}</div>
              <div className="font-heading text-3xl font-bold text-slate-900" data-testid="profile-name">{user.name}</div>
              {user.verified_entity && (
                <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold border border-blue-200" data-testid="profile-verified-entity-badge">
                  <ShieldCheck size={13} /> Ente verificato
                  {user.verified_entity_kind && <span className="ml-1 opacity-80">· {
                    { civil_protection: "Protezione Civile", fire_dept: "Vigili del Fuoco", municipality: "Comune", other: "altro ente autorizzato" }[user.verified_entity_kind] || user.verified_entity_kind
                  }</span>}
                </div>
              )}
              <div className="text-sm text-slate-500 mt-1 flex items-center gap-3">
                <span><Trophy size={12} className="inline mr-1 text-yellow-500" /> {t("reputation_short")} {user.reputation || 0}</span>
              </div>
              {/* Phase 5 — public profiles are shareable */}
              <div className="mt-2">
                <ShareButton
                  path={`/profile/${user.user_id}`}
                  title={user.name}
                  description={t("chat_group_member")}
                  testid="profile-share"
                />
              </div>
            </div>
            {authUser && !isSelf && (
              <div className="flex flex-col gap-2" data-testid="profile-actions">
                <ReportUserButton
                  reportedUserId={user.user_id}
                  reportedUserName={user.name}
                  contextType="profile"
                  contextRef={user.user_id}
                  label={t("report_user")}
                  testid={`profile-report-user-${user.user_id}`}
                  variant="outline"
                />
                <BlockUserButton
                  targetUserId={user.user_id}
                  testid={`profile-block-user-${user.user_id}`}
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4 mt-8">
            <Stat label={t("stat_reports_created")} value={user.stats?.reports_created || 0} testid="stat-reports" />
            <Stat label={t("stat_confirmations_made")} value={user.stats?.confirmations || 0} testid="stat-confirmations" />
            <Stat label={t("stat_reputation")} value={user.reputation || 0} testid="stat-reputation" />
          </div>

          {/* Phase 4 — Social links (public read + owner-only edit) */}
          <div className="mt-6">
            <SocialLinksList links={user.social_links} />
            {isSelf && (
              <div className="mt-6 rounded-2xl border border-slate-200 p-4">
                <SocialLinksEditor
                  initial={user.social_links || []}
                  isAdult={!!authUser?.is_adult}
                  onSaved={(u) => setUser({ ...user, social_links: u?.social_links || [] })}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl bg-emerald-50/60 border border-emerald-100 p-5" data-testid={testid}>
      <div className="font-heading text-3xl font-bold text-emerald-800 tabular-nums">{value}</div>
      <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  );
}
